// The team: the reps on the phones and in the field. Add, rename, give a
// color, mark inactive. A rep with stores cannot be deleted, only made
// inactive, so the log keeps their name.
import React, { useState } from 'react';
import { Check, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { deleteRep, saveRep } from './salesData';
import { Field, REP_COLORS, REP_COLOR_KEYS, RepChip, Sheet, btnGhost, btnPrimary, btnSmall, inputCls } from './salesUi';

// What a rep sells decides which stores the Arena puts in front of them (sql/sales-pipeline/14).
const SELLS = [['', 'Everything'], ['websites', 'Websites: restaurants, food trucks'], ['furniture', 'Furniture platform']];

export default function TeamModal({ client, storeId, reps, onChange, onClose, notify, meId, onPickMe }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [niche, setNiche] = useState('');
  const [color, setColor] = useState(REP_COLOR_KEYS[reps.length % REP_COLOR_KEYS.length]);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const rep = await saveRep(client, { store_id: storeId, name: name.trim(), phone: phone.trim() || null, email: email.trim().toLowerCase() || null, color, active: true, niche: niche || null });
      onChange([...reps, rep]);
      setName('');
      setPhone('');
      setEmail('');
      setColor(REP_COLOR_KEYS[(reps.length + 1) % REP_COLOR_KEYS.length]);
      notify(`${rep.name} added to the team`);
    } catch (e) {
      console.error('sales: add rep failed', e);
      notify('Could not add that rep', 'error');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (rep, changes) => {
    setBusy(true);
    try {
      const saved = await saveRep(client, { ...rep, ...changes });
      onChange(reps.map((r) => (r.id === saved.id ? saved : r)));
    } catch (e) {
      console.error('sales: rep update failed', e);
      notify('Could not save that change', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rep) => {
    if (!window.confirm(`Remove ${rep.name} from the team? Their stores stay and become unassigned.`)) return;
    setBusy(true);
    try {
      await deleteRep(client, rep.id);
      onChange(reps.filter((r) => r.id !== rep.id));
      if (meId === rep.id) onPickMe('');
    } catch (e) {
      console.error('sales: rep delete failed', e);
      notify('Could not remove that rep', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title="The team" subtitle="Everyone who makes calls and visits. Each rep picks their own name at the top of the Sales tab on their device.">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Add a rep</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={inputCls} onKeyDown={(e) => e.key === 'Enter' && add()} />
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Their cell, goes in the scripts" className={inputCls} onKeyDown={(e) => e.key === 'Enter' && add()} />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their email, sends from the app" className={inputCls} onKeyDown={(e) => e.key === 'Enter' && add()} />
          <select value={niche} onChange={(e) => setNiche(e.target.value)} className={inputCls} aria-label="What they sell" title="What they sell: the Arena only shows them those stores">
            {SELLS.map(([k, label]) => <option key={k} value={k}>{k ? `Sells ${label.toLowerCase()}` : 'Sells everything'}</option>)}
          </select>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            {REP_COLOR_KEYS.map((k) => (
              <button key={k} type="button" onClick={() => setColor(k)} className={`w-7 h-7 rounded-full ${REP_COLORS[k]} flex items-center justify-center ring-offset-2 ${color === k ? 'ring-2 ring-slate-400' : ''}`} aria-label={k}>
                {color === k && <Check className="w-4 h-4 text-white" />}
              </button>
            ))}
          </div>
          <button type="button" onClick={add} disabled={!name.trim() || busy} className={btnPrimary}>{busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add</button>
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {reps.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No one on the team yet.</div>}
        {reps.map((r) => (
          <div key={r.id} className={`px-4 py-3 ${r.active === false ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-3">
              <RepChip rep={r} size="lg" />
              <input defaultValue={r.name} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== r.name && patch(r, { name: e.target.value.trim() })} className="flex-1 min-w-[80px] bg-transparent font-semibold text-slate-900 text-sm focus:outline-none focus:bg-slate-50 rounded px-1 -mx-1" aria-label="Name" />
              {meId === r.id ? (
                <span className="text-[11px] font-bold text-blue-700 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 whitespace-nowrap">You</span>
              ) : (
                <button type="button" onClick={() => onPickMe(r.id)} className={`${btnSmall} whitespace-nowrap`} title="This is me on this device">This is me</button>
              )}
              <button type="button" disabled={busy} onClick={() => patch(r, { active: r.active === false })} className={btnSmall} title={r.active === false ? 'Back on the team' : 'Off the team for now; their stores keep their name'}>{r.active === false ? 'Activate' : 'Pause'}</button>
              <button type="button" disabled={busy} onClick={() => remove(r)} className="w-8 h-8 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-700 flex items-center justify-center flex-shrink-0" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input type="tel" defaultValue={r.phone || ''} placeholder="Cell, goes in the scripts" onBlur={(e) => (e.target.value.trim() || null) !== (r.phone || null) && patch(r, { phone: e.target.value.trim() || null })} className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500" aria-label="Cell" />
              <input type="email" defaultValue={r.email || ''} placeholder="Email, sends from the app" onBlur={(e) => (e.target.value.trim().toLowerCase() || null) !== (r.email || null) && patch(r, { email: e.target.value.trim().toLowerCase() || null })} className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500" aria-label="Email" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] text-slate-400">Sells</span>
              <select value={r.niche || ''} disabled={busy} onChange={(e) => patch(r, { niche: e.target.value || null })} className="h-8 px-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-800 focus:outline-none" aria-label={`What ${r.name} sells`} title="The Arena only shows them these stores">
                {SELLS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div className="mt-2 flex items-center gap-1">
              <span className="text-[11px] text-slate-400 mr-1">Color</span>
              {REP_COLOR_KEYS.map((k) => (
                <button key={k} type="button" disabled={busy} onClick={() => patch(r, { color: k })} className={`w-5 h-5 rounded-full ${REP_COLORS[k]} ${r.color === k ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`} aria-label={k} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
